import {
  type Currency,
  countryDisplayName,
  ISO_COUNTRY_CODES,
  parseAmount,
  SHIPPING_DELIVERY_ESTIMATE_MAX,
  type ShippingDestination,
} from "@loot-os/api-contract";

import { amountInputValue } from "./currency";

/** One editable row of the destinations table; `cost` is the typed amount, parsed on save. */
export interface DestinationRow {
  /** Stable React key across reorders; never sent. */
  key: string;
  countryCode: string;
  enabled: boolean;
  cost: string;
  deliveryEstimate: string;
}

export type DestinationField = "countryCode" | "cost" | "deliveryEstimate";

export interface DestinationIssue {
  key: string;
  field: DestinationField;
  message: string;
}

/** Every country the picker offers, by name. Names come from the runtime, never a stored label. */
export const ISO_COUNTRY_OPTIONS: readonly { code: string; name: string }[] = ISO_COUNTRY_CODES.map(
  (code) => ({ code, name: countryDisplayName(code) }),
).sort((a, b) => a.name.localeCompare(b.name));

function rowKey(): string {
  return crypto.randomUUID();
}

/** Stored destinations → editable rows, the cost in the currency's display units. */
export function toRows(
  destinations: readonly ShippingDestination[],
  currency: Pick<Currency, "multiplier">,
): DestinationRow[] {
  return destinations.map((destination) => ({
    key: rowKey(),
    countryCode: destination.countryCode,
    enabled: destination.enabled,
    cost: amountInputValue(destination.costCents, currency),
    deliveryEstimate: destination.deliveryEstimate,
  }));
}

/** A blank row for "Add country": no country yet, enabled, free, no estimate. */
export function newRow(currency: Pick<Currency, "multiplier">): DestinationRow {
  return {
    key: rowKey(),
    countryCode: "",
    enabled: true,
    cost: amountInputValue(0, currency),
    deliveryEstimate: "",
  };
}

/**
 * Rows → the destinations to save, or the first thing wrong with them, named by row and field
 * so the table can point at it: a country left unpicked or picked twice, a cost that is not a
 * non-negative amount in this currency, a blank or over-long estimate.
 */
export function toDestinations(
  rows: readonly DestinationRow[],
  currency: Pick<Currency, "symbol" | "multiplier">,
): { ok: true; destinations: ShippingDestination[] } | { ok: false; issue: DestinationIssue } {
  const destinations: ShippingDestination[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const issue = (field: DestinationField, message: string) =>
      ({ ok: false, issue: { key: row.key, field, message } }) as const;
    if (!row.countryCode) return issue("countryCode", "Pick a country.");
    if (seen.has(row.countryCode)) return issue("countryCode", "This country is already listed.");
    seen.add(row.countryCode);
    const costCents = parseAmount(row.cost, currency);
    if (costCents === null || costCents < 0) {
      return issue("cost", "Enter a cost of zero or more.");
    }
    const deliveryEstimate = row.deliveryEstimate.trim();
    if (!deliveryEstimate) return issue("deliveryEstimate", "Say how long delivery takes.");
    if (deliveryEstimate.length > SHIPPING_DELIVERY_ESTIMATE_MAX) {
      return issue(
        "deliveryEstimate",
        `Keep it under ${SHIPPING_DELIVERY_ESTIMATE_MAX} characters.`,
      );
    }
    destinations.push({
      countryCode: row.countryCode,
      enabled: row.enabled,
      costCents,
      deliveryEstimate,
    });
  }
  return { ok: true, destinations };
}

/** The rows with the one at `index` moved by `delta` places; unchanged when that leaves the list. */
export function moveRow(
  rows: readonly DestinationRow[],
  index: number,
  delta: -1 | 1,
): DestinationRow[] {
  const target = index + delta;
  if (index < 0 || index >= rows.length || target < 0 || target >= rows.length) return [...rows];
  const next = [...rows];
  const [moved] = next.splice(index, 1);
  if (moved) next.splice(target, 0, moved);
  return next;
}

/** Whether saving `rows` would change anything: compares what would be saved, not how it is typed. */
export function rowsDirty(
  rows: readonly DestinationRow[],
  baseline: readonly ShippingDestination[],
  currency: Pick<Currency, "symbol" | "multiplier">,
): boolean {
  const parsed = toDestinations(rows, currency);
  // Rows that do not parse cannot equal anything saved.
  if (!parsed.ok) return true;
  return JSON.stringify(parsed.destinations) !== JSON.stringify(baseline);
}
